import React, { useEffect, useState, useCallback } from 'react';
import {
  ALL_MODULES,
  DEFAULT_FIELDS_BY_COLUMN_NAME,
  InlineEdit,
  InlineEditTableWrapper,
  Loader,
  StatusDropdown,
  ApiProgressLoader,
  AsyncPropSelector
} from '@nile/components';
import { DisabledTag } from '@nile/mangifera/src/Pebbles/TicketContainer';
import styled from 'styled-components';
import { SVGIcon } from '@superops/super-icons';
import { getClientSiteVariablesById } from '@nile/graphql';

let timerId = null;

function InlineEditTextCellV2(props) {
  const { _ticket, cell, editCellId } = props;
  const ticket = _ticket || cell.row.original;
  const {
    actions,
    type,
    editable,
    clearable,
    shouldFreezeCell,
    setEditCell
  } = cell.column;
  const CELL_TYPE = type === 'Status' ? 'tag' : '';
  const isCellFreezed = shouldFreezeCell && shouldFreezeCell(cell);
  const isEditable = !isCellFreezed && editable;
  const [apiProgress, setApiProgress] = useState(false);
  const [apiResult, setApiResult] = useState(null);
  const setApiComplete = (state) => {
    setApiProgress(false);
    setApiResult(state);
    if (timerId) clearTimeout(timerId);
    timerId = setTimeout(() => {
      setApiResult(null);
    }, 2000);
  };

  const displayVal = cell
    ? props.column.subAccessor
      ? cell.value[props.column.subAccessor]
      : cell.value
      ? cell.value.name || cell.value
      : ''
    : ticket[accessor]
    ? typeof ticket[accessor] === 'object'
      ? ticket[accessor].name
        ? ticket[accessor].name
        : ticket[accessor].firstName
        ? ticket[accessor].firstName + ' ' + ticket[accessor].lastName
        : ticket[accessor].label
      : ticket[accessor]
    : '';

  const updateFieldState = (value) => {
    actions.save && actions.save(value, ticket, props.data, setApiComplete);
    setApiProgress(true);
    setEditCell(null);
  };

  if (editCellId && editCellId === cell.id) {
    return (
      <InlineEditTableWrapper
        className="full-width"
        key={ticket?.ticketId || cell?.field?.name || cell?.row?.id}
      >
        <EditTextCell {...props} updateFieldState={updateFieldState} />
      </InlineEditTableWrapper>
    );
  }

  return (
    <div
      className={`flex center full-height mw-100`}
      onClick={(e) => {
        if (isEditable) {
          setEditCell(cell.id);
          e.stopPropagation();
          e.preventDefault();
        }
      }}
    >
      <div
        className={`ellipsis self-center flex g5 center inlineSelect full-height max-width-250 ${
          isEditable ? 'editable-cell pointer' : 'cell'
        }`}
        title={displayVal}
      >
        <div className="ellipsis">
          {displayVal || <DisabledTag>NA</DisabledTag>}
        </div>
        {apiProgress && <ApiProgressLoader isLoading />}
        {apiResult && <ApiProgressLoader isSuccess={apiResult === 'success'} />}

        {isEditable && (
          <div className="visibilityActions edit-actions pointer">
            {
              <div className="ml8 fx center">
                <SVGIcon name="Edit" />
              </div>
            }
          </div>
        )}
      </div>
    </div>
  );
}

function EditTextCell(props) {
  const {
    cell,
    params,
    _ticket,
    mode = 'view',
    updateFieldState,
    type,
    isClearable
  } = props;
  const allProps = params || cell.column;
  const {
    editable,
    field,
    actions,
    accessor,
    entity,
    autoSave,
    parentField,
    dependantField,
    shouldFreezeCell
  } = allProps;
  const isCellFreezed = shouldFreezeCell && shouldFreezeCell(cell);
  const ticket = _ticket || cell?.row?.original;
  const isEditable = !isCellFreezed && editable;
  const [popoverActive, setPopoverActive] = useState(false);
  const [updatedProperties, setUpdatedProperties] = useState({});
  const togglePopoverActive = useCallback(() => {
    setPopoverActive(!popoverActive);
  });
  const activator = (
    <div className="ml8 fx center" onClick={() => togglePopoverActive()}>
      <SVGIcon name="Edit" />
    </div>
  );

  const updateSelectedProperties = (changedProperties) => {
    const newProperties = {
      ...updatedProperties,
      ...changedProperties
    };
    setUpdatedProperties(newProperties);
    updateFieldState(newProperties);
  };

  const updateSelection = (f, val) => {
    let serialisedValue = null;
    let updatedProperty = {};
    if (field.isCustom) {
      updateSelectedProperties('customFields', val);
    } else {
      if (val) {
        switch (field.name) {
          case 'techGroup':
            serialisedValue = { groupId: val.groupId, name: val.name };
            break;
          case 'technician':
            serialisedValue = { userId: val.userId };
            break;
          case 'sop':
            serialisedValue = { sopId: val.sopId, name: val.name };
            break;
          case 'site':
            serialisedValue = { id: val.id, name: val.name };
            break;
          default:
            serialisedValue = val.name;
        }
      }
      updatedProperty = { [field.name]: serialisedValue };
      if (parentField.name === f.name && dependantField) {
        updatedProperty = {
          ...updatedProperty,
          [dependantField.name]: null
        };
      }
      updateSelectedProperties(updatedProperty);
    }
  };

  const getVariables = () => {
    switch (field.name) {
      case 'site':
      case 'site.name':
        return ticket?.client?.accountId
          ? getClientSiteVariablesById(ticket.client.accountId)
          : null;
      default:
        return undefined;
    }
  };

  //TODO :: render value in a proper way for NA - vignesh
  const displayVal = cell
    ? props.column.subAccessor
      ? cell.value[props.column.subAccessor]
      : cell.value
      ? cell.value.name || cell.value
      : ''
    : ticket[accessor]
    ? typeof ticket[accessor] === 'object'
      ? ticket[accessor].name
        ? ticket[accessor].name
        : ticket[accessor].firstName
        ? ticket[accessor].firstName + ' ' + ticket[accessor].lastName
        : ticket[accessor].label
      : ticket[accessor]
    : '';

  const fieldValue = updatedProperties[field.name] || ticket[field.name];
  const defaultValue = fieldValue
    ? typeof fieldValue === 'object'
      ? {
          name: fieldValue.name,
          label: fieldValue.name,
          value: fieldValue.name
        }
      : { name: fieldValue, label: fieldValue, value: fieldValue }
    : '';

  const parentFieldValue = updatedProperties.hasOwnProperty(parentField?.name)
    ? updatedProperties[parentField.name]
    : ticket[parentField?.name];
  return (
    <div className="inline-editable-cell editable-cell pl8 pr8 visibilityContainer">
      <AsyncPropSelector
        defaultValue={defaultValue}
        updateSelection={updateSelection}
        parentValue={parentFieldValue}
        ticket={ticket}
        field={field}
        entity={entity || 'Ticket'}
        variables={getVariables()}
        hideTitle
        defaultMenuIsOpen
        isClearable={isClearable}
      />
    </div>
  );
}

export default InlineEditTextCellV2;
